'use client';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CTA_DEFAULT_MIN_WIDTH } from '@ui/button';
import { interpolate } from '@format/interpolate';
import { Modal } from '@web/components/Modal';
import { VideoModal } from '@web/components/VideoModal';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { useCarouselModalPause } from '../../hooks/CarouselPauseContext';
import { TrackedLink } from '../TrackedLink';
import { ResponsiveImage } from '../ResponsiveImage';
import { StyledCtaButton, CtaImageContainer } from './styles.js';
import { ctaButtonClicked } from './events';

export const CtaButton = ({
  children,
  href,
  triggerModal = false,
  modal,
  variant = 'primary',
  onClick,
  fullWidth = false,
  fullWidthMobile = false,
  fullWidthDesktop = false,
  openNewTab = false,
  minWidth = CTA_DEFAULT_MIN_WIDTH,
  headerText = '',
  alignment = 'center',
  'data-testid': dataTestId,
  icon = null,
  iconAlignment = 'left',
}) => {
  const [showModal, setShowModal] = useState(false);
  const { formattedAmount } = useShopifyOfferContext();
  const onModalOpenChange = useCarouselModalPause();
  const onModalOpenChangeRef = useRef(onModalOpenChange);
  onModalOpenChangeRef.current = onModalOpenChange;
  const isModalOpenRef = useRef(false);

  // Only notify pause when this CTA actually had a modal open (avoids ref-count drift).
  useEffect(() => {
    if (!triggerModal) return undefined;
    return () => {
      if (!isModalOpenRef.current) return;
      isModalOpenRef.current = false;
      onModalOpenChangeRef.current?.(false);
    };
  }, [triggerModal]);

  const openModal = () => {
    setShowModal(true);
    if (isModalOpenRef.current) return;
    isModalOpenRef.current = true;
    // Sync notify — waiting for useEffect leaves a window where autoplay can still tick.
    onModalOpenChangeRef.current?.(true);
  };

  const closeModal = () => {
    setShowModal(false);
    if (!isModalOpenRef.current) return;
    isModalOpenRef.current = false;
    onModalOpenChangeRef.current?.(false);
  };

  const getCtaPosition = href => {
    const params = new URLSearchParams(href?.split('?')[1]);
    return params.get('cta_position');
  };

  const ctaPosition = getCtaPosition(href);
  const text = typeof children === 'string' ? children : null;
  const eventPayload = ctaButtonClicked(ctaPosition, href, text, headerText);

  const iconNode = icon?.src ? <ResponsiveImage {...icon} /> : null;

  return (
    // Would it make sense for CTA button to just extend LinkButton rather than wrap it?
    <StyledCtaButton
      $alignment={alignment}
      $fullWidth={fullWidth}
      $fullWidthMobile={fullWidthMobile}
      $fullWidthDesktop={fullWidthDesktop}
    >
      <TrackedLink
        href={href}
        variant={variant}
        horizontalPadding={6}
        verticalPadding={0}
        minWidth={minWidth}
        onClick={
          triggerModal
            ? e => {
                e.preventDefault();
                openModal();
              }
            : onClick
        }
        fullWidth={fullWidth}
        fullWidthMobile={fullWidthMobile}
        fullWidthDesktop={fullWidthDesktop}
        target={openNewTab ? '_blank' : null}
        rel={openNewTab ? 'noopener noreferrer' : null}
        headerText={headerText}
        analyticsEvent={eventPayload}
        data-testid={dataTestId}
      >
        {iconAlignment === 'left' && iconNode && (
          <CtaImageContainer $position="left">{iconNode}</CtaImageContainer>
        )}
        {typeof children === 'string'
          ? interpolate(children, { offer: formattedAmount ?? '50%' })
          : children}
        {iconAlignment === 'right' && iconNode && (
          <CtaImageContainer $position="right">{iconNode}</CtaImageContainer>
        )}
      </TrackedLink>
      {triggerModal && modal && modal.component === 'video_modal' ? (
        <VideoModal shown={showModal} onClickClose={closeModal} {...modal} />
      ) : (
        <Modal shown={showModal} onCloseModal={closeModal} {...modal} />
      )}
    </StyledCtaButton>
  );
};

CtaButton.displayName = 'CtaButton';

CtaButton.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
  fullWidthDesktop: PropTypes.bool,
  openNewTab: PropTypes.bool,
  minWidth: PropTypes.number,
  headerText: PropTypes.string,
  alignment: PropTypes.string,
  'data-testid': PropTypes.string,
  triggerModal: PropTypes.bool,
  modal: PropTypes.object,
  icon: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  iconAlignment: PropTypes.oneOf(['left', 'right']),
};
